import { useState, useRef } from "react";
import { uploadFile, getUploadUrl, deleteUpload } from "@/lib/api";
import { Camera, X, SpinnerGap, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable photo upload component.
 * @param {string[]} fileIds - Array of GridFS file IDs already uploaded
 * @param {function} onChange - Called with updated fileIds array
 * @param {number} max - Max number of photos (default 1 for licence, higher for inspections)
 * @param {string} label - Label text
 * @param {boolean} disabled - Whether upload is disabled
 */
export function PhotoUpload({ fileIds = [], onChange, max = 1, label = "Photo", disabled = false }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = max - fileIds.length;
    const toUpload = files.slice(0, remaining);
    if (toUpload.length === 0) {
      toast.error(`Maximum ${max} photo${max > 1 ? 's' : ''} allowed`);
      return;
    }

    setUploading(true);
    const newIds = [...fileIds];
    for (const file of toUpload) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10 MB)`);
        continue;
      }
      try {
        const result = await uploadFile(file);
        newIds.push(result.file_id);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    onChange(newIds);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemove = async (fileId) => {
    try {
      await deleteUpload(fileId);
    } catch {}
    onChange(fileIds.filter(id => id !== fileId));
  };

  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>

      {/* Existing photos */}
      {fileIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {fileIds.map((fid) => (
            <div key={fid} className="relative group w-20 h-20 rounded-md overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src={getUploadUrl(fid)}
                alt="Upload"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              {!disabled && (
                <button
                  type="button"
                  data-testid={`photo-remove-${fid}`}
                  onClick={() => handleRemove(fid)}
                  className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white rounded-bl-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X weight="bold" className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {!disabled && fileIds.length < max && (
        <label
          data-testid={`photo-upload-${label.toLowerCase().replace(/\s+/g, '-')}`}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-md cursor-pointer transition-colors
            ${uploading
              ? 'bg-slate-100 text-slate-400 cursor-wait'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
            }`}
        >
          {uploading ? (
            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Uploading...</>
          ) : (
            <><Camera weight="bold" className="w-4 h-4" /> {fileIds.length === 0 ? `Add ${label}` : `Add More`}</>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            multiple={max > 1}
            onChange={handleFiles}
            disabled={disabled || uploading}
            className="hidden"
          />
        </label>
      )}

      {/* Placeholder when no photos and disabled */}
      {disabled && fileIds.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <ImageIcon weight="duotone" className="w-4 h-4" /> No photo uploaded
        </div>
      )}
    </div>
  );
}
